// api.js: Laadkaart API client
const API_URL = process.env.REACT_APP_API_URL || 'https://strategisch-laadplan-backend-production.up.railway.app';

// ── De sleutel van de ingelogde gebruiker ──────────────────────────────
// Blijft alleen in het geheugen van deze pagina staan, niet in de browseropslag.
// Wie de pagina ververst, logt daarmee opnieuw in. Dat was al zo en verandert
// hier niet.
let sleutel = null;
let ingelogdeGebruiker = null;

export function zetSleutel(nieuweSleutel, gebruiker = null) {
  sleutel = nieuweSleutel;
  ingelogdeGebruiker = gebruiker;
}

export function getIngelogdeGebruiker() {
  return ingelogdeGebruiker;
}

// Zet de sleutel bij elk verzoek in de kop. Zonder sleutel blijft de kop leeg
// en gedraagt het verzoek zich zoals voorheen.
function metSleutel(headers = {}) {
  return sleutel ? { ...headers, Authorization: `Bearer ${sleutel}` } : { ...headers };
}

// Leest het antwoord en geeft een begrijpelijke melding als het geen gegevens
// zijn. Dat gebeurt bijvoorbeeld wanneer de frontend al bijgewerkt is en de
// server nog niet: dan komt er een HTML-foutpagina terug in plaats van json,
// en een kale melding over een onverwacht teken zegt niemand iets.
async function leesAntwoord(resp, pad) {
  const tekst = await resp.text();
  let data = null;
  try { data = JSON.parse(tekst); } catch { /* geen json, zie hieronder */ }

  if (data === null) {
    if (resp.status === 404) {
      throw new Error(
        `De server kent dit onderdeel nog niet (${pad}). ` +
        `Waarschijnlijk draait er nog een oudere versie van de server; ` +
        `werk die bij en probeer opnieuw.`);
    }
    throw new Error(
      `De server gaf geen leesbaar antwoord (status ${resp.status}). ` +
      `Controleer of de server draait.`);
  }
  if (!resp.ok) {
    const fout = new Error(data.error || `Serverfout ${resp.status}`);
    // Bij een botsing stuurt de server mee wie wat wanneer wijzigde. Dat moet
    // het scherm bereiken, anders kan het alleen "er ging iets mis" tonen.
    if (data.botsing) fout.botsing = data.botsing;
    throw fout;
  }
  return data;
}

async function apiFetch(path, options = {}) {
  const resp = await fetch(`${API_URL}${path}`, {
    headers: metSleutel({ 'Content-Type': 'application/json', ...options.headers }),
    ...options,
  });
  return leesAntwoord(resp, path);
}

export async function getAlleGemeenten() { return apiFetch('/gemeenten'); }
export async function getGemeente(id)    { return apiFetch(`/gemeenten/${id}`); }
export async function getFluviusPrive(id) { return apiFetch(`/geo/fluvius-prive/${id}`); }
export async function getVlootverhouding(id) { return apiFetch(`/gemeenten/${encodeURIComponent(id)}/vlootverhouding`); }
export async function getGemeentenLijst(land = 'België') { return apiFetch(`/geo/gemeenten-lijst?land=${encodeURIComponent(land)}`); }
export async function getPostcodesVoorNis(nis) { return apiFetch(`/geo/postcodes/${encodeURIComponent(nis)}`); }
export async function getBevolking(naam) { return apiFetch(`/geo/bevolking/${encodeURIComponent(naam)}`); }
export async function verversBevolkingscijfers() { return apiFetch('/geo/ververs-bevolking', { method:'POST' }); }
// Alleen het inwonertal van één gemeente. Voor een bewerker, die het landelijke
// verversen hierboven niet mag.
export async function verversBevolkingGemeente(id) { return apiFetch(`/gemeenten/${encodeURIComponent(id)}/ververs-bevolking`, { method:'POST' }); }
export async function slaGemeenteOp(g)  { return apiFetch('/gemeenten', { method:'POST', body:JSON.stringify(g) }); }
export async function updateGemeente(id, data) { return apiFetch(`/gemeenten/${id}`, { method:'PUT', body:JSON.stringify(data) }); }
export async function verwijderGemeente(id) { return apiFetch(`/gemeenten/${id}`, { method:'DELETE' }); }
export async function updateWijk(gid, wid, data) { return apiFetch(`/gemeenten/${gid}/wijken/${wid}`, { method:'PATCH', body:JSON.stringify(data) }); }
export async function getStats()  { return apiFetch('/stats'); }
export async function checkHealth() {
  try { return await apiFetch('/health'); }
  catch { return { status: 'offline' }; }
}

// ── Geo API calls ────────────────────────────────────────────────────
export async function getNisCode(naam, land = 'België') {
  return apiFetch(`/geo/nis-lookup?naam=${encodeURIComponent(naam)}&land=${encodeURIComponent(land)}`);
}

export async function onboardGemeenteGeo(id, nisCode = null) {
  return apiFetch(`/geo/onboard/${id}`, {
    method: 'POST',
    body: JSON.stringify({ nisCode }),
  });
}

export async function getSectoren(id) {
  return apiFetch(`/geo/sectoren/${id}`);
}

export async function syncWijkenVanStatbel(id) {
  return apiFetch(`/geo/sync-wijken/${id}`, { method: 'POST' });
}

export async function getLaadpalen(id) {
  return apiFetch(`/geo/laadpalen/${id}`);
}

// ── Gegevensbronnen ────────────────────────────────────────────────────
// De externe bronnen weigeren verzoeken vanaf onze server, dus leest de
// gebruiker de bestanden zelf in. Deze twee sturen het bestand als ruwe
// inhoud mee, niet als json, en zetten daarom geen Content-Type header.

export async function getBronnen() {
  return apiFetch('/bronnen');
}

// Leest het bestand alleen om te tonen wat erin zit; slaat niets op.
export async function controleerBron(bron, bestand) {
  const pad = `/bronnen/${bron}/controleer?bestandsnaam=${encodeURIComponent(bestand.name)}`;
  const resp = await fetch(`${API_URL}${pad}`, { method: 'POST', headers: metSleutel(), body: bestand });
  return leesAntwoord(resp, pad);
}

// Leest het bestand definitief in. De vorige versie blijft bewaard.
export async function importeerBron(bron, bestand, peildatum) {
  const params = new URLSearchParams({ bestandsnaam: bestand.name });
  if (peildatum) params.set('peildatum', peildatum);
  const pad = `/bronnen/${bron}/import?${params}`;
  const resp = await fetch(`${API_URL}${pad}`, { method: 'POST', headers: metSleutel(), body: bestand });
  try {
    return await leesAntwoord(resp, pad);
  } catch (e) {
    // De meldingen tot aan de fout blijven bruikbaar voor het procesvenster
    try {
      const data = JSON.parse(await resp.clone().text());
      if (data?.stappen) e.stappen = data.stappen;
    } catch { /* geen json, dan is er ook geen stappenlijst */ }
    throw e;
  }
}

// Alle rijen van een bron uit de actieve versie, eventueel voor één gemeente.
export async function getBronRijen(bron, gemeente = null) {
  const q = gemeente ? `?gemeente=${encodeURIComponent(gemeente)}` : '';
  return apiFetch(`/bronnen/${bron}/rijen${q}`);
}

// ── Laadpleinen ────────────────────────────────────────────────────────
// De parkeerterreinen worden door de server zelf opgehaald bij de open
// kaartdienst, meteen na het onboarden. Deze twee lezen ze uit en laten het
// opnieuw doen als dat de eerste keer niet lukte.
export async function getLaadpleinTerreinen(gemeenteId) {
  return apiFetch(`/laadpleinen/${gemeenteId}`);
}

// ── Instellingen per gemeente ──────────────────────────────────────────
// De beleidskeuzes van één gemeente. Wat je meestuurt wordt bewaard, de rest
// blijft staan, dus één schuif bijwerken overschrijft de andere niet.
export async function getInstellingen(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/instellingen`);
}

// gezien is wat dit scherm zag toen het openging. Wijkt dat af van wat er nu
// op de server staat, dan is er iemand anders geweest en weigert de server de
// opslag in plaats van die wijziging stil te overschrijven.
export async function bewaarInstellingen(gemeenteId, instellingen, gezien = null) {
  return apiFetch(`/gemeenten/${gemeenteId}/instellingen`, {
    method: 'PUT',
    body: JSON.stringify(gezien ? { instellingen, gezien } : { instellingen }),
  });
}

// Het wijzigingslogboek van één gemeente. Voor iedereen met toegang tot die
// gemeente, ook een lezer.
export async function getWijzigingen(gemeenteId, opties = {}) {
  const vragen = new URLSearchParams();
  if (opties.aantal) vragen.set('aantal', opties.aantal);
  if (opties.dagen) vragen.set('dagen', opties.dagen);
  const q = vragen.toString();
  return apiFetch(`/wijzigingen/${encodeURIComponent(gemeenteId)}${q ? '?' + q : ''}`);
}

// Wie welk uitgangspunt als laatste wijzigde, en wanneer.
export async function getInstellingenHerkomst(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/instellingen/herkomst`);
}

// ── Straten ────────────────────────────────────────────────────────────
// De straten van een gemeente, uitgedund tot één punt per 25 meter. Nodig om
// een geplotte laadpaal naar de dichtstbijzijnde straat te trekken. De namen
// komen apart mee, met per punt het nummer uit die lijst.
export async function getStraten(gemeenteId) {
  return apiFetch(`/straten/${gemeenteId}`);
}

export async function haalStratenOp(gemeenteId, gemeentenaam) {
  return apiFetch(`/straten/${gemeenteId}/ophalen?gemeente=${encodeURIComponent(gemeentenaam)}`,
    { method: 'POST' });
}

// ── Rapporten ──────────────────────────────────────────────────────────
// Stuurt de tabel zoals hij in beeld staat naar de server en zet het opgemaakte
// Excel-bestand klaar om te bewaren. De bestandsnaam komt van de server mee.
// ── Adressen van de gemeente ───────────────────────────────────────────
// Uit het Adressenregister van Vlaanderen. Nodig om te weten waar mensen
// wonen, fijner dan het inwonertal per statistische sector.
export async function getAdressen(gemeenteId) {
  return apiFetch(`/adressen/${encodeURIComponent(gemeenteId)}`);
}

export async function haalAdressenOp(gemeenteId, gemeentenaam) {
  return apiFetch(`/adressen/${encodeURIComponent(gemeenteId)}/ophalen`
    + `?gemeente=${encodeURIComponent(gemeentenaam)}`, { method: 'POST' });
}

// ── Highlights teaser ──────────────────────────────────────────────────
// Staat de teaser aan voor deze gemeente en dit account. Nee is het normale
// antwoord: hij wordt per keer aangezet in de beheeromgeving.
export async function getTeaserStatus(gemeenteId) {
  return apiFetch(`/teaser/${encodeURIComponent(gemeenteId)}/status`);
}

// Haalt het PowerPoint-bestand op en biedt het aan als download.
export async function downloadTeaser(gemeenteId, gegevens, standaardnaam = 'teaser.pptx') {
  const resp = await fetch(`${API_URL}/teaser/${encodeURIComponent(gemeenteId)}/pptx`, {
    method: 'POST',
    headers: metSleutel({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(gegevens),
  });
  if (!resp.ok) {
    let melding = `Serverfout ${resp.status}`;
    try { melding = (JSON.parse(await resp.text())).error || melding; } catch { /* geen json */ }
    throw new Error(melding);
  }
  const beschikking = resp.headers.get('Content-Disposition') || '';
  const naam = (beschikking.match(/filename="([^"]+)"/) || [])[1] || standaardnaam;
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
  return naam;
}

export async function downloadRapport(opdracht, soort = 'xlsx') {
  const resp = await fetch(`${API_URL}/rapport/${soort}`, {
    method: 'POST',
    headers: metSleutel({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(opdracht),
  });
  if (!resp.ok) {
    let melding = `Serverfout ${resp.status}`;
    try { melding = (JSON.parse(await resp.text())).error || melding; } catch { /* geen json */ }
    throw new Error(melding);
  }
  const beschikking = resp.headers.get('Content-Disposition') || '';
  const naam = (beschikking.match(/filename="([^"]+)"/) || [])[1] || `rapport.${soort}`;
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  a.click();
  URL.revokeObjectURL(a.href);
  return naam;
}

// Het technisch fiche van één locatie als PDF. Het scherm stuurt de schets en
// de kengetallen mee; de server tekent en zet het bestand klaar om te bewaren.
export async function downloadFichePdf(opdracht) {
  const resp = await fetch(`${API_URL}/rapport/fiche-pdf`, {
    method: 'POST',
    headers: metSleutel({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(opdracht),
  });
  if (!resp.ok) {
    let melding = `Serverfout ${resp.status}`;
    try { melding = (JSON.parse(await resp.text())).error || melding; } catch { /* geen json */ }
    throw new Error(melding);
  }
  const beschikking = resp.headers.get('Content-Disposition') || '';
  const naam = (beschikking.match(/filename="([^"]+)"/) || [])[1] || 'technisch-fiche.pdf';
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  a.click();
  URL.revokeObjectURL(a.href);
  return naam;
}

// ── Historie van opgeleverde bestanden ─────────────────────────────────
export async function getExportHistorie(gemeenteId) {
  return apiFetch(`/rapport/historie/${gemeenteId}`);
}

// Een bestand dat het scherm zelf heeft gemaakt in de historie zetten.
export async function bewaarExport(opdracht) {
  return apiFetch('/rapport/bewaar', { method: 'POST', body: JSON.stringify(opdracht) });
}

// Een eerder opgeleverd bestand opnieuw ophalen, exact zoals het toen was.
export async function downloadHistorie(id) {
  const resp = await fetch(`${API_URL}/rapport/historie/bestand/${id}`, { headers: metSleutel() });
  if (!resp.ok) throw new Error(`Dit bestand kon niet worden opgehaald (${resp.status}).`);
  const beschikking = resp.headers.get('Content-Disposition') || '';
  const naam = (beschikking.match(/filename="([^"]+)"/) || [])[1] || 'bestand';
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  a.click();
  URL.revokeObjectURL(a.href);
  return naam;
}

export async function haalLaadpleinTerreinenOp(gemeenteId, gemeentenaam) {
  return apiFetch(`/laadpleinen/${gemeenteId}/ophalen?gemeente=${encodeURIComponent(gemeentenaam)}`,
    { method: 'POST' });
}

// ── Beoordeling van kandidaatlocaties ──────────────────────────────────
export async function getLaadpleinOordelen(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/laadpleinen`);
}

// status is 'geschikt', 'niet', 'later' of 'open'; die laatste zet de
// beoordeling terug. De opstelling (aantal palen per type) is optioneel: laat
// je hem weg, dan blijft wat er op de server staat ongemoeid. Dat is met opzet,
// zodat een klik op een beoordelingsknop nooit een ingevulde opstelling wist.
// Hetzelfde geldt voor het jaar en voor deelwagens, het aantal LAADPUNTEN dat
// op deze locatie voor deelwagens wordt voorbehouden.
// Het achtste argument is het technisch fiche: de ingetekende parkeervakken,
// welke daarvan zijn aangewezen, waar de palen staan en waar de cabine komt.
// Zelfde regel als de rest: weglaten laat de bestaande schets staan, null wist
// hem.
export async function zetLaadpleinOordeel(gemeenteId, locatieId, status, notitie = null,
                                          opstelling = undefined, jaar = undefined,
                                          deelwagens = undefined, fiche = undefined,
                                          bestelwagens = undefined) {
  const lichaam = { status, notitie };
  if (opstelling !== undefined) lichaam.opstelling = opstelling;
  // Alleen meesturen als het jaar echt is opgegeven; anders blijft de
  // bestaande waarde op de server staan.
  if (jaar !== undefined) lichaam.jaar = jaar;
  if (deelwagens !== undefined) lichaam.deelwagens = deelwagens;
  if (fiche !== undefined) lichaam.fiche = fiche;
  // Laadpunten voor bestelwagens: { ac, dc }, of null om te wissen.
  if (bestelwagens !== undefined) lichaam.bestelwagens = bestelwagens;
  return apiFetch(`/gemeenten/${gemeenteId}/laadpleinen/${encodeURIComponent(locatieId)}`, {
    method: 'PUT',
    body: JSON.stringify(lichaam),
  });
}

// ── De aansluitingskaart van de netbeheerder ───────────────────────────
export async function getNetcongestieStand() {
  return apiFetch('/netcongestie/stand');
}

// De kaart gaat als ruwe afbeelding naar de server; de hoekcoördinaten en de
// peildatum staan in het adres. Als tekst zou het bestand een derde groter
// worden, en dat is bij een kaart van heel Vlaanderen een merkbaar verschil.
export async function stuurNetcongestieKaart(png, hoeken, peildatum, bestandsnaam) {
  const vragen = new URLSearchParams({
    latBoven: String(hoeken.latBoven), latOnder: String(hoeken.latOnder),
    lonLinks: String(hoeken.lonLinks), lonRechts: String(hoeken.lonRechts),
    peildatum: peildatum || '', bestandsnaam: bestandsnaam || '',
  });
  const resp = await fetch(`${API_URL}/netcongestie/kaart?${vragen}`, {
    method: 'POST',
    headers: metSleutel({ 'Content-Type': 'application/octet-stream' }),
    body: png,
  });
  return leesAntwoord(resp, '/netcongestie/kaart');
}

// ── Netcapaciteit ──────────────────────────────────────────────────────
// De aansluitwaarden staan in de backend, zodat ze op één plek staan en een
// latere toepassing voor netbeheerders dezelfde bron gebruikt.
export async function getNetcapaciteitParameters() {
  return apiFetch('/netcapaciteit/parameters');
}

// Stuurt de laadpunten per wijk per jaar en de opstellingen per laadplein op;
// de server rekent het vermogen, de cabines en de jaarreeks uit en bewaart die.
export async function bewaarNetcapaciteit(gemeenteId, opdracht) {
  return apiFetch(`/gemeenten/${gemeenteId}/netcapaciteit`, {
    method: 'POST', body: JSON.stringify(opdracht),
  });
}

// De inwoners per statistische sector van een gemeente, uit de ingelezen
// Statbel-bron. Nodig om te kunnen zeggen hoeveel mensen er buiten de dekking
// van een laadpunt wonen.
// De wijken van een gemeente volgens de gemeentegedragen wijkindeling, met
// inwoners en auto's uit de sectoren. Werkt op een NIS-code, dus ook voordat
// de gemeente in de app bestaat.
export async function getWijkindeling(nisOfNaam) {
  return apiFetch(`/geo/wijkindeling/${encodeURIComponent(nisOfNaam)}`);
}

export async function getInwonersSectoren(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/inwoners-sectoren`);
}

// De laadbehoefte voor bestelwagens volgens MOW, voor de hele gemeente, in kW.
// Wordt alleen getoond, niet meegerekend.
export async function getMowBestelwagens(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/mow-bestelwagens`);
}

export async function getNetcapaciteit(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/netcapaciteit`);
}

// ── GIS netbeheerder ───────────────────────────────────────────────────
// De netgegevens van Fluvius per gemeente (GeoPackage). De server leest alleen
// de lagen van het stroomnet en bewaart het bestand zelf niet. Zie gisnet.js.
export async function getGisnet(gemeenteId, metLagen = false) {
  return apiFetch(`/gemeenten/${gemeenteId}/gisnet${metLagen ? '?lagen=1' : ''}`);
}

export async function laadGisnet(gemeenteId, bestand) {
  const pad = `/gemeenten/${gemeenteId}/gisnet?bestandsnaam=${encodeURIComponent(bestand.name)}`;
  const resp = await fetch(`${API_URL}${pad}`, { method: 'POST', headers: metSleutel(), body: bestand });
  return leesAntwoord(resp, pad);
}

export async function verwijderGisnet(gemeenteId) {
  return apiFetch(`/gemeenten/${gemeenteId}/gisnet`, { method: 'DELETE' });
}

// Afstanden tot het net berekenen en bewaren. soort is 'plein' of 'paal';
// punten is een lijst { sleutel, lat, lng }. Zonder netgegevens komt alleen
// terug wat eerder al bewaard was.
export async function berekenNetAfstanden(gemeenteId, soort, punten) {
  return apiFetch(`/gemeenten/${gemeenteId}/gisnet/afstanden`, {
    method: 'POST', body: JSON.stringify({ soort, punten }),
  });
}

export async function getNetAfstanden(gemeenteId, soort = '') {
  return apiFetch(`/gemeenten/${gemeenteId}/gisnet/afstanden${soort ? `?soort=${soort}` : ''}`);
}

// ── Bezetting ──────────────────────────────────────────────────────────
// Of het tabblad Bezetting voor deze gebruiker in beeld mag. De server beslist:
// de rol beheerder, of iedereen zodra BEZETTING_VOOR_IEDEREEN op 1 staat.
export async function getBezettingZichtbaar() {
  return apiFetch('/bezetting/zichtbaar');
}

// De gemeten bezetting van één gemeente. laadsoort is 'gewoon' of 'snel';
// bezet is 'laden' of 'laden_geblokkeerd'; drempel in procent, of leeg voor de
// drempel die bij de gemeente bewaard is.
export async function getBezetting(gemeenteId, laadsoort = 'gewoon', bezet = 'laden', drempel = null) {
  const q = new URLSearchParams({ laadsoort, bezet });
  if (drempel != null) q.set('drempel', String(drempel));
  return apiFetch(`/gemeenten/${encodeURIComponent(gemeenteId)}/bezetting?${q}`);
}

// Het overzicht van de bezetting per week, maand, kwartaal en jaar als
// Excel-bestand, met dezelfde schakelaars als het scherm. wijkIds: de wijken
// die meetellen, in de volgorde van het scherm.
export async function downloadBezettingExcel(gemeenteId, { laadsoort = 'gewoon', bezet = 'laden', drempel = null,
                                                          wijkIds = [] } = {}) {
  const q = new URLSearchParams({ laadsoort, bezet });
  if (drempel != null) q.set('drempel', String(drempel));
  if (wijkIds.length) q.set('wijken', wijkIds.join(','));
  const pad = `/gemeenten/${encodeURIComponent(gemeenteId)}/bezetting/excel?${q}`;
  const resp = await fetch(`${API_URL}${pad}`, { headers: metSleutel() });
  if (!resp.ok) {
    let melding = `Serverfout ${resp.status}`;
    try { melding = (JSON.parse(await resp.text())).error || melding; } catch { /* geen json */ }
    throw new Error(melding);
  }
  const beschikking = resp.headers.get('Content-Disposition') || '';
  const naam = (beschikking.match(/filename="([^"]+)"/) || [])[1] || 'bezetting.xlsx';
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
  return naam;
}

// De drempel voor de bezetting bewaren bij de gemeente, in procent.
export async function bewaarBezettingDrempel(gemeenteId, drempel) {
  return apiFetch(`/gemeenten/${encodeURIComponent(gemeenteId)}/bezetting/drempel`, {
    method: 'PUT',
    body: JSON.stringify({ drempel }),
  });
}

// De huishoudens per gemeente laten ophalen bij Provincies in Cijfers. De
// server slaat ze op als nieuwe versie van de bron, net als een ingelezen
// bestand. Bij een mislukking gaan de stappen mee voor het procesvenster.
export async function haalHuishoudensOp() {
  const pad = '/bronnen/huishoudens/ophalen';
  const resp = await fetch(`${API_URL}${pad}`, { method: 'POST', headers: metSleutel() });
  // De tekst één keer lezen: daarna is het antwoord op en kan het niet nog
  // eens gelezen worden voor de stappen.
  const tekst = await resp.text();
  let data = null;
  try { data = JSON.parse(tekst); } catch { /* hieronder gemeld */ }
  if (data === null) {
    throw new Error(resp.status === 404
      ? `De server kent dit onderdeel nog niet (${pad}). Waarschijnlijk draait er nog een oudere versie van de server.`
      : `De server gaf geen leesbaar antwoord (status ${resp.status}).`);
  }
  if (!resp.ok) {
    const fout = new Error(data.error || `Serverfout ${resp.status}`);
    if (data.stappen) fout.stappen = data.stappen;
    throw fout;
  }
  return data;
}

// De groei van het aantal auto's per jaar voor één gemeente, uit de
// huishoudens. factoren is null als de bron ontbreekt; reden zegt dan waarom.
export async function getHuishoudgroei(gemeenteId) {
  return apiFetch(`/gemeenten/${encodeURIComponent(gemeenteId)}/huishoudgroei`);
}

export async function zetBronVersieTerug(bron, importId) {
  return apiFetch(`/bronnen/${bron}/terugzetten/${importId}`, { method: 'POST' });
}

// ── Inloggen en gebruikersbeheer ───────────────────────────────────────
// Het inloggen gebeurt op de server. De browser bewaart alleen de sleutel die
// hij terugkrijgt, en stuurt die bij elk volgend verzoek mee.

export async function inloggen(gebruikersnaam, wachtwoord) {
  const antwoord = await apiFetch('/auth/inloggen', {
    method: 'POST',
    body: JSON.stringify({ gebruikersnaam, wachtwoord }),
  });
  zetSleutel(antwoord.sleutel, antwoord.gebruiker);
  return antwoord.gebruiker;
}

export async function uitloggen() {
  try { await apiFetch('/auth/uitloggen', { method: 'POST' }); }
  finally { zetSleutel(null, null); }
}

export async function wieBenIk() { return apiFetch('/auth/ik'); }

// Opent de beheeromgeving in een nieuw tabblad, met de inlog van nu. De sleutel
// gaat mee achter het hekje in het adres: dat deel stuurt de browser niet naar
// de server, en beheer.js haalt het er meteen weer uit. Zo hoeft de beheerder
// niet een tweede keer in te loggen.
export function openBeheeromgeving() {
  // Het adres van LaadAtlas zelf gaat mee, zodat de knop Hoofdmenu in de
  // beheeromgeving hier terug kan komen.
  const terug = encodeURIComponent(window.location.origin);
  const adres = sleutel ? `${API_URL}/beheer#sessie=${encodeURIComponent(sleutel)}&terug=${terug}`
                        : `${API_URL}/beheer`;
  window.open(adres, '_blank', 'noopener');
}

// Bij de eerste inlog kiest de gebruiker zelf een wachtwoord. Twee keer, zodat
// een typefout niet leidt tot een account waar niemand meer in komt.
export async function zetEersteWachtwoord(nieuw, herhaling) {
  return apiFetch('/auth/eerste-wachtwoord', {
    method: 'POST', body: JSON.stringify({ nieuw, herhaling }),
  });
}

export async function wijzigEigenWachtwoord(huidig, nieuw) {
  return apiFetch('/auth/wachtwoord', {
    method: 'POST', body: JSON.stringify({ huidig, nieuw }),
  });
}

export async function getGebruikers() { return apiFetch('/gebruikers'); }

export async function maakGebruiker(gegevens) {
  return apiFetch('/gebruikers', { method: 'POST', body: JSON.stringify(gegevens) });
}

export async function wijzigGebruiker(id, gegevens) {
  return apiFetch(`/gebruikers/${id}`, { method: 'PUT', body: JSON.stringify(gegevens) });
}

export async function zetWachtwoord(id, wachtwoord) {
  return apiFetch(`/gebruikers/${id}/wachtwoord`, {
    method: 'POST', body: JSON.stringify({ wachtwoord }),
  });
}

export async function verwijderGebruiker(id) {
  return apiFetch(`/gebruikers/${id}`, { method: 'DELETE' });
}

export async function getInlogpogingen(aantal = 200) {
  return apiFetch(`/inlogpogingen?aantal=${aantal}`);
}

// ── Monitoring Module Handhaving ─────────────────────────────────────
// Alle adressen staan in handhaving.js op de server. Met een account (de
// beheerder) gaat de inlog mee in de kop, zoals bij de rest van LaadAtlas.
export async function getHandhavingGemeenten() { return apiFetch('/handhaving/beheer/gemeenten'); }
// semi: ook semi-publieke laadpunten meetellen. Standaard alleen publieke.
export async function getHandhavingAnalyse(id, semi = false) {
  return apiFetch(`/handhaving/${encodeURIComponent(id)}/analyse${semi ? '?semi=1' : ''}`);
}
// Hulp bij kiezen grens: cijfers per grens met de ingevulde controle-uren en
// regels voor terugkerend, zonder iets op te slaan.
export async function getHandhavingGrenshulp(id, { controle, weken, minimum, keren }, semi = false) {
  const q = new URLSearchParams({ controle: String(controle), weken: String(weken), minimum: String(minimum), keren: String(keren) });
  if (semi) q.set('semi', '1');
  return apiFetch(`/handhaving/${encodeURIComponent(id)}/grenshulp?${q.toString()}`);
}
// Het verloop per week, maand of kwartaal.
export async function getHandhavingTrend(id, soort, semi = false) {
  return apiFetch(`/handhaving/${encodeURIComponent(id)}/trend?soort=${encodeURIComponent(soort)}${semi ? '&semi=1' : ''}`);
}
export async function getHandhavingBestanden(id) { return apiFetch(`/handhaving/${encodeURIComponent(id)}/bestanden`); }
export async function bewaarHandhavingInstellingen(id, velden) {
  return apiFetch(`/handhaving/beheer/${encodeURIComponent(id)}/instellingen`, { method: 'PUT', body: JSON.stringify(velden) });
}
export async function leesHandhavingBestandIn(id, soort, bestand) {
  const pad = `/handhaving/beheer/${encodeURIComponent(id)}/inlezen?soort=${encodeURIComponent(soort)}`
    + `&bestandsnaam=${encodeURIComponent(bestand.name || '')}`;
  const resp = await fetch(`${API_URL}${pad}`, {
    method: 'POST', headers: metSleutel({ 'Content-Type': 'application/octet-stream' }), body: bestand,
  });
  return leesAntwoord(resp, pad);
}
// soort: 'excel', 'word' of 'exploitant' (dan is extra de naam van de exploitant).
export async function downloadHandhaving(id, soort, extra = null, semi = false) {
  const vraag = [];
  if (soort === 'exploitant') vraag.push(`naam=${encodeURIComponent(extra || '')}`);
  if (semi) vraag.push('semi=1');
  const pad = `/handhaving/${encodeURIComponent(id)}/${soort}` + (vraag.length ? `?${vraag.join('&')}` : '');
  const resp = await fetch(`${API_URL}${pad}`, { headers: metSleutel() });
  if (!resp.ok) {
    let melding = `Serverfout ${resp.status}`;
    try { melding = (JSON.parse(await resp.text())).error || melding; } catch { /* geen json */ }
    throw new Error(melding);
  }
  const beschikking = resp.headers.get('Content-Disposition') || '';
  const naam = (beschikking.match(/filename="([^"]+)"/) || [])[1] || `handhaving.${soort === 'word' ? 'docx' : 'xlsx'}`;
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
  return naam;
}
